<template>
  <div class="survey-form-container mt-5">
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else-if="!survey" class="text-center py-5 text-muted">
      <h4>Survey not found or not published.</h4>
    </div>

    <div v-else-if="submitted" class="text-center py-5 glass-panel">
      <i class="fa fa-check-circle text-success" style="font-size: 4rem; margin-bottom: 1rem;"></i>
      <h3 class="fw-bold">Thank You!</h3>
      <p class="text-muted">Your response has been recorded successfully.</p>
      <button class="btn btn-primary-gradient mt-3 px-4 rounded-pill" @click="$router.push('/survey-management')">Return
        to Surveys</button>
    </div>

    <div v-else class="glass-panel p-5 mx-auto" style="max-width: 800px;">
      <div class="text-center mb-5">
        <h2 class="fw-bold text-dark mb-2">{{ survey.title }}</h2>
        <p class="text-muted">{{ survey.description }}</p>
      </div>

      <form @submit.prevent="submitSurvey">
        <div v-for="(q, index) in survey.questions" :key="index" class="question-block mb-4">
          <label class="form-label fw-bold mb-3" style="font-size: 1.1rem;">
            {{ index + 1 }}. {{ q.question_text }}
            <span v-if="q.is_required" class="text-danger">*</span>
          </label>

          <!-- Short Text -->
          <input v-if="q.question_type === 'Short Text'" type="text" class="form-control tc-app-input"
            v-model="answers[q.id]" :required="q.is_required" placeholder="Type your answer here..." />

          <!-- Number -->
          <input v-if="q.question_type === 'Number'" type="number" class="form-control tc-app-input"
            v-model="answers[q.id]" :required="q.is_required" placeholder="Enter a number..." />

          <!-- Rating Scale (1 to 5) -->
          <div v-if="q.question_type === 'Rating Scale'" class="rating-scale d-flex gap-3 mt-3">
            <label v-for="n in 5" :key="n" class="rating-box shadow-sm" :class="{ 'active': answers[q.id] === n }">
              <input type="radio" class="d-none" :name="'rating-' + q.id" :value="n" v-model="answers[q.id]"
                :required="q.is_required" />
              {{ n }}
            </label>
          </div>

          <!-- Single Select / Radio Button -->
          <div v-if="q.question_type === 'Single Select' || q.question_type === 'Radio Button'"
            class="d-flex flex-column gap-3 mt-2">
            <label v-for="(opt, oIndex) in q.options" :key="oIndex" class="tc-app-option-card shadow-sm"
              :class="{ 'selected': answers[q.id] === opt }">
              <input type="radio" class="d-none" :name="'single-' + q.id" :value="opt" v-model="answers[q.id]"
                :required="q.is_required" />
              <div class="custom-radio-indicator"></div>
              <span class="option-text">{{ opt }}</span>
            </label>
          </div>

          <!-- Multi Select -->
          <div v-if="q.question_type === 'Multi Select'" class="d-flex flex-column gap-3 mt-2">
            <label v-for="(opt, oIndex) in q.options" :key="oIndex" class="tc-app-option-card shadow-sm"
              :class="{ 'selected': (answers[q.id] || []).includes(opt) }">
              <input type="checkbox" class="d-none" :value="opt" v-model="answers[q.id]" />
              <div class="custom-check-indicator"><i class="fa fa-check" v-if="(answers[q.id] || []).includes(opt)"></i>
              </div>
              <span class="option-text">{{ opt }}</span>
            </label>
          </div>

          <!-- Dropdown -->
          <div v-if="q.question_type === 'Dropdown'" class="mt-2">
            <select class="form-select tc-app-select shadow-sm" v-model="answers[q.id]" :required="q.is_required">
              <option value="" disabled selected>Select an option...</option>
              <option v-for="(opt, oIndex) in q.options" :key="oIndex" :value="opt">{{ opt }}</option>
            </select>
          </div>

        </div>

        <div class="mt-5 text-end">
          <button type="submit" class="btn btn-primary-gradient btn-lg rounded-pill px-5 shadow">Submit Response <i
              class="fa fa-arrow-right ms-2"></i></button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SurveyForm',
  data() {
    return {
      survey: null,
      loading: true,
      submitted: false,
      answers: {}
    };
  },
  async mounted() {
    await this.fetchSurvey();
    window.addEventListener('online', this.syncOfflineResponses);
  },
  beforeDestroy() {
    window.removeEventListener('online', this.syncOfflineResponses);
  },
  methods: {
    async fetchSurvey() {
      const id = this.$route.params.id;
      let data = null;
      try {
        const baseUrl = process.env.VUE_APP_BASE_URL || 'http://localhost:1337';
        const token = localStorage.getItem('jwt');
        const res = await axios.get(`${baseUrl}/api/surveys/${id}?populate=questions`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        data = res.data.data;
        localStorage.setItem(`survey_${id}`, JSON.stringify(data));
      } catch (e) {
        console.error("Survey fetch failed, falling back to cache", e);
        const cached = localStorage.getItem(`survey_${id}`);
        if (cached) {
          data = JSON.parse(cached);
        } else {
          this.$bvToast.toast('You are offline and this survey is not cached.', {
            title: 'Offline',
            variant: 'warning',
            solid: true
          });
        }
      } finally {
        this.loading = false;
      }

      if (data && data.attributes.publishedAt) {
        this.survey = { id: data.id, ...data.attributes };

        const initialAnswers = {};
        this.survey.questions.forEach(q => {
          if (q.question_type === 'Multi Select') {
            initialAnswers[q.id] = [];
          } else {
            initialAnswers[q.id] = null;
          }
        });
        this.answers = initialAnswers;
      }
    },
    async submitSurvey() {
      try {
        for (const q of this.survey.questions) {
          if (q.is_required && q.question_type === 'Multi Select' && this.answers[q.id].length === 0) {
            this.$bvToast.toast(`Please answer question: ${q.question_text}`, {
              title: 'Required Field',
              variant: 'warning',
              solid: true
            });
            return;
          }
        }

        const payload = {
          data: {
            survey: this.survey.id,
            answers: this.answers
          }
        };
        const token = localStorage.getItem('jwt');
        const baseUrl = process.env.VUE_APP_BASE_URL || 'http://localhost:1337';

        if (!navigator.onLine) {
          this.saveOfflineResponse(payload);
          return;
        }

        await axios.post(`${baseUrl}/api/survey-responses`, payload, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        this.submitted = true;
      } catch (e) {
        if (!e.response) { // Network error fallback
           this.saveOfflineResponse({
             data: { survey: this.survey.id, answers: this.answers }
           });
           return;
        }
        console.error(e);
        this.$bvToast.toast('Failed to submit survey. Ensure you have the required permissions.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    },
    getOfflineStorageKey() {
      const userProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      const userId = userProfile.id || 'anonymous';
      return `offline_survey_responses_${userId}`;
    },
    saveOfflineResponse(payload) {
      const key = this.getOfflineStorageKey();
      const offlineResponses = JSON.parse(localStorage.getItem(key) || '[]');
      offlineResponses.push(payload);
      localStorage.setItem(key, JSON.stringify(offlineResponses));
      this.submitted = true;
      this.$bvToast.toast('You are offline. Response saved locally and will sync when online.', {
        title: 'Offline Mode',
        variant: 'info',
        solid: true
      });
    },
    async syncOfflineResponses() {
      const key = this.getOfflineStorageKey();
      const offline = JSON.parse(localStorage.getItem(key) || '[]');
      if (offline.length === 0) return;

      this.$bvToast.toast(`Syncing ${offline.length} offline responses...`, {
        title: 'Syncing',
        variant: 'info',
        solid: true
      });

      const token = localStorage.getItem('jwt');
      const baseUrl = process.env.VUE_APP_BASE_URL || 'http://localhost:1337';
      
      let successCount = 0;
      for (const payload of offline) {
        try {
          await axios.post(`${baseUrl}/api/survey-responses`, payload, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          successCount++;
        } catch (e) {
          console.error('Failed to sync response', e);
        }
      }

      if (successCount === offline.length) {
        localStorage.removeItem(key);
        this.$bvToast.toast('All offline responses synced successfully!', {
          title: 'Sync Complete',
          variant: 'success',
          solid: true
        });
      } else {
        const remaining = offline.slice(successCount);
        localStorage.setItem(key, JSON.stringify(remaining));
        this.$bvToast.toast(`Synced ${successCount} responses. Some failed.`, {
          title: 'Sync Partial',
          variant: 'warning',
          solid: true
        });
      }
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');

.survey-form-container {
  font-family: 'Outfit', sans-serif;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  background-color: #F8F9FC;
  min-height: 100vh;
}

.text-gradient {
  background: linear-gradient(135deg, #6C5DD3, #8a7df0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.btn-primary-gradient {
  background: #6C5DD3;
  border: none;
  color: white;
  font-weight: 600;
  transition: transform 0.2s, box-shadow 0.2s;
}

.btn-primary-gradient:hover {
  transform: translateY(-2px);
  background: #5b4eb8;
  color: white;
}

.glass-panel {
  background: #ffffff;
  border: 1px solid #E2E2EA;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
}

.question-block {
  background: white;
  padding: 2rem;
  border-radius: 16px;
  border: 1px solid #E2E2EA;
  margin-bottom: 2rem;
}

.tc-app-input {
  background-color: #ffffff;
  border: 1px solid #E2E2EA;
  border-radius: 12px;
  padding: 1rem 1.5rem;
  font-size: 1.05rem;
  color: #11142D;
  transition: all 0.2s;
}

.tc-app-input:focus {
  border-color: #6C5DD3;
  box-shadow: 0 0 0 0.25rem rgba(108, 93, 211, 0.15);
  background-color: #ffffff;
}

/* Custom Rating Scale */
.rating-scale .rating-box {
  width: 55px;
  height: 55px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: 600;
  border: 1px solid #E2E2EA;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s;
  color: #B2B2BF;
  background: #ffffff;
}

.rating-scale .rating-box:hover {
  border-color: #6C5DD3;
  color: #6C5DD3;
  transform: translateY(-2px);
}

.rating-scale .rating-box.active {
  background: #6C5DD3;
  color: white;
  border-color: #6C5DD3;
}

/* Custom Cards for Radios & Checkboxes */
.tc-app-option-card {
  display: flex;
  align-items: center;
  padding: 1.2rem 1.5rem;
  background-color: #ffffff;
  border: 1px solid #E2E2EA;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tc-app-option-card:hover {
  border-color: #6C5DD3;
  background-color: #F8F9FC;
}

.tc-app-option-card.selected {
  border-color: #6C5DD3;
  background-color: #F0EDFF;
}

/* Radio Indicator */
.custom-radio-indicator {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid #B2B2BF;
  margin-right: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
}

.tc-app-option-card.selected .custom-radio-indicator {
  border-color: #6C5DD3;
  background-color: #6C5DD3;
}

.tc-app-option-card.selected .custom-radio-indicator::after {
  content: '';
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: white;
}

/* Checkbox Indicator */
.custom-check-indicator {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 2px solid #B2B2BF;
  margin-right: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  color: white;
  font-size: 12px;
  flex-shrink: 0;
}

.tc-app-option-card.selected .custom-check-indicator {
  border-color: #6C5DD3;
  background-color: #6C5DD3;
}

.option-text {
  font-size: 1.05rem;
  color: #11142D;
  font-weight: 500;
}

/* Custom Select Dropdown */
.tc-app-select {
  appearance: none;
  background-color: #ffffff;
  border: 1px solid #E2E2EA;
  border-radius: 12px;
  padding: 1.2rem 1.5rem;
  font-size: 1.05rem;
  font-weight: 500;
  color: #11142D;
  cursor: pointer;
  transition: all 0.2s;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%236C5DD3' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1.5rem center;
  background-size: 16px 12px;
}

.tc-app-select:focus {
  border-color: #6C5DD3;
  box-shadow: 0 0 0 0.25rem rgba(108, 93, 211, 0.15);
  outline: none;
}
</style>
